import {
  BookOpen,
  ClipboardList,
  Clock,
  Maximize2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCountdown } from "@/hooks/useCountdown";
import { cn } from "@/lib/cn";
import type { Language } from "@/lib/types";

interface Props {
  exerciseTitle: string;       // "Sliding Window Rate Limiter"
  language: Language;
  remainingSec: number | null;
  fullscreen: boolean;
  /** Si false, Instrucciones queda disabled (pre-start). */
  instructionsAvailable?: boolean;
  /** Progreso entre ejercicios. Solo se muestra si total > 1. */
  progress?: { current: number; total: number };
  onInstructions: () => void;
  onDocs: () => void;
  onToggleFullscreen: () => void;
}

const LANG_LABEL: Record<Language, string> = {
  typescript: "TypeScript",
  javascript: "JavaScript",
  sql: "SQL",
};

export function TestHeader({
  exerciseTitle,
  language,
  remainingSec,
  fullscreen,
  instructionsAvailable = true,
  progress,
  onInstructions,
  onDocs,
  onToggleFullscreen,
}: Props) {
  const low = remainingSec != null && remainingSec < 60;
  return (
    <header className="flex items-start justify-between gap-4 mb-8">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          {progress && (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground bg-secondary border border-border px-2 py-1 rounded-md tracking-wider">
              Ejercicio{" "}
              <strong className="text-foreground">{progress.current}</strong>
              <span className="text-muted-foreground/70">/</span>
              <span className="text-muted-foreground/90">{progress.total}</span>
            </span>
          )}
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          {exerciseTitle} — {LANG_LABEL[language]}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {remainingSec != null && (
          <div
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-3 h-9 text-sm font-mono tabular-nums",
              low
                ? "border-destructive/40 bg-destructive/10 text-destructive animate-pulse"
                : "border-border bg-card text-foreground"
            )}
          >
            <Clock className="h-3.5 w-3.5" />
            {formatCountdown(remainingSec)}
          </div>
        )}
        <Button
          variant="default"
          size="md"
          onClick={onInstructions}
          disabled={!instructionsAvailable}
          title={
            !instructionsAvailable
              ? "Disponible una vez que comiences el ejercicio"
              : undefined
          }
        >
          <ClipboardList className="h-4 w-4" />
          Instrucciones
        </Button>
        <Button variant="teal" size="md" onClick={onDocs}>
          <BookOpen className="h-4 w-4" />
          Docs
        </Button>
        <Button variant="primary" size="md" onClick={onToggleFullscreen}>
          {fullscreen ? (
            <>
              <X className="h-4 w-4" />
              Salir pantalla completa
            </>
          ) : (
            <>
              <Maximize2 className="h-4 w-4" />
              Pantalla completa
            </>
          )}
        </Button>
      </div>
    </header>
  );
}
